import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import JobApplicationForm from "@/components/feature/JobApplicationForm";

export default function GeneralApplyPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Careers", href: "/careers" },
          { label: "Submit Your Resume" },
        ]}
      />

      {/* Hero */}
      <section className="relative py-16 md:py-20 bg-hero-gradient overflow-hidden">
        <div className="container-astro relative z-10 text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
              <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">
                General Application
              </span>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={100}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-6 font-heading tracking-tight">
              Submit Your Resume
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-2xl mx-auto">
              Don&apos;t see the right role? Send us your resume and tell us how you could
              contribute to AstroJal.
            </p>
          </AnimatedSection>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
      </section>

      {/* Form */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-astro">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {/* Info panel */}
            <div className="lg:col-span-1 flex flex-col gap-4">
              <AnimatedSection>
                <div className="rounded-2xl bg-background-100 border border-background-200/60 p-6 md:p-7">
                  <h2 className="text-lg font-bold text-foreground-950 mb-3 font-heading">
                    What Happens Next
                  </h2>
                  <ul className="flex flex-col gap-3.5">
                    {[
                      { icon: "ri-file-search-line", title: "We review your resume", desc: "Every application is reviewed personally by our team." },
                      { icon: "ri-user-add-line", title: "We add you to our talent pool", desc: "Great matches are kept on file for future roles." },
                      { icon: "ri-chat-3-line", title: "We reach out", desc: "We contact you when the right opportunity opens up." },
                    ].map((item) => (
                      <li key={item.title} className="flex items-start gap-3">
                        <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                          <i className={`${item.icon} text-lg`} />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{item.title}</p>
                          <p className="text-xs text-foreground-600 leading-relaxed mt-0.5">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={100}>
                <div className="rounded-2xl bg-background-100 border border-background-200/60 p-6 md:p-7">
                  <h2 className="text-lg font-bold text-foreground-950 mb-3 font-heading">
                    Application Tips
                  </h2>
                  <ul className="flex flex-col gap-2.5">
                    {[
                      "Keep your resume to 1-2 pages.",
                      "Highlight Laravel, PHP, Flutter or cloud experience.",
                      "Share links to your GitHub or portfolio.",
                    ].map((tip) => (
                      <li key={tip} className="flex items-start gap-2.5 text-sm text-foreground-600 leading-relaxed">
                        <span className="w-5 h-5 mt-0.5 shrink-0 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-600">
                          <i className="ri-check-line text-xs" />
                        </span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={150}>
                <a
                  href="/careers"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-background-200/70 text-foreground-700 text-sm font-semibold rounded-xl hover:bg-background-100 transition-all whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-xs" />
                  Back to Open Positions
                </a>
              </AnimatedSection>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <div className="rounded-2xl bg-background-100 border border-background-200/60 p-6 md:p-8">
                  <div className="mb-7">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2 font-heading">
                      General Application
                    </h2>
                    <p className="text-sm text-foreground-600">
                      Tell us about yourself and how you could contribute to AstroJal.
                    </p>
                  </div>
                  <JobApplicationForm role="General Application" submitLabel="Submit Your Resume" />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}